// Publishing (task 5): where the type goes (the store for public types, the
// organisation's own Git repository for private ones), its versions, the
// AI review and the owner's confirmation.
import { useEffect, useState } from "preact/hooks";
import { Icon } from "./icons";
import type { Actions, WorkbenchData } from "./model";
import type { Version } from "../lib/types";
import { GitSettings } from "./GitSettings";
import { t } from "../i18n";

const statusStyle: Record<Version["status"], string> = {
  ready: "bg-sel text-accent2",
  reviewed: "bg-violet-soft text-violet",
  publishing: "bg-sel text-accent2",
  submitted: "bg-sel text-accent2",
  in_review: "bg-warn-soft text-warn",
  checks_failed: "bg-crit-soft text-crit",
  published: "bg-good-soft text-good",
  pushed: "bg-good-soft text-good",
  rejected: "bg-crit-soft text-crit",
  failed: "bg-crit-soft text-crit",
};

const statusText = (st: Version["status"]): string =>
  ({
    ready: t("pub.status.ready"),
    reviewed: t("pub.status.reviewed"),
    publishing: t("pub.status.publishing"),
    submitted: t("pub.status.submitted"),
    in_review: t("pub.status.in_review"),
    checks_failed: t("pub.status.checks_failed"),
    published: t("pub.status.published"),
    pushed: t("pub.status.pushed"),
    rejected: t("pub.status.rejected"),
    failed: t("pub.status.failed"),
  })[st];

const severity = (s: string): string =>
  s === "blocker" ? t("pub.sev.blocker") : s === "warning" ? t("pub.sev.warning") : s === "note" ? t("pub.sev.note") : s;

function nextVersion(versions: Version[], manifest: string): string {
  const all = versions.map((v) => v.version).concat(manifest ? [manifest] : []);
  if (versions.length === 0) return manifest || "1.0.0";
  const top = all.map((v) => v.split(".").map(Number)).sort((a, b) => b[0] - a[0] || b[1] - a[1] || b[2] - a[2])[0];
  return `${top[0]}.${top[1] + 1}.0`;
}

export function PublishView({ data, actions, writer }: { data: WorkbenchData; actions: Actions; writer: boolean }) {
  const p = data.project;
  const [versions, setVersions] = useState<Version[] | null>(null);
  const [manifestVersion, setManifestVersion] = useState("");
  const [target, setTarget] = useState({ target: p.publish_target });
  const [ver, setVer] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState<Record<string, { ok: boolean; issues: boolean }>>({});

  const load = () =>
    actions.versions().then((vs) => {
      setVersions(vs);
      return vs;
    });
  useEffect(() => {
    load().catch((e) => setErr((e as Error).message));
    actions.readFile("manifest.yaml").then((m) => setManifestVersion(m.match(/^version:\s*"?([^"\s]+)/m)?.[1] ?? "")).catch(() => {});
  }, [p.head_rev]);
  useEffect(() => {
    if (versions && !ver) setVer(nextVersion(versions, manifestVersion));
  }, [versions, manifestVersion]);

  const act = async (what: string, fn: () => Promise<unknown>) => {
    setBusy(what);
    setErr("");
    try {
      await fn();
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy("");
    }
  };
  const blocking = data.summary.blocking;
  const storeOK = data.me.storePublisher;

  return (
    <div class="min-h-0 flex-1 overflow-y-auto">
      <div class="mx-auto flex max-w-[960px] flex-col gap-8 px-6 py-8">
        <header>
          <h2 class="m-0 text-[20px] font-semibold">{t("pub.title", { name: p.name })}</h2>
          <p class="mb-0 mt-1.5 text-muted">{t("pub.intro")} <a href="/help/versions-publishing" target="_blank">{t("pub.how")}</a>.</p>
        </header>

        <section class="rounded-xl border border-line bg-side p-5">
          <h3 class="m-0 text-[15px] font-semibold">{t("pub.destination")}</h3>
          <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label class={`flex cursor-pointer gap-3 rounded-lg border p-3 ${target.target === "git" ? "border-accent bg-sel/40" : "border-line2"}`}>
              <input type="radio" name="target" checked={target.target === "git"} disabled={!writer} onChange={() => setTarget({ ...target, target: "git" })} />
              <span><span class="block font-semibold">{t("pub.git.title")}</span><span class="text-[12.5px] text-muted">{t("pub.git.text")}</span></span>
            </label>
            <label class={`flex gap-3 rounded-lg border p-3 ${storeOK ? "cursor-pointer" : "opacity-60"} ${target.target === "store" ? "border-accent bg-sel/40" : "border-line2"}`}>
              <input type="radio" name="target" checked={target.target === "store"} disabled={!writer || !storeOK} onChange={() => setTarget({ ...target, target: "store" })} />
              <span><span class="block font-semibold">gIoT Store</span><span class="text-[12.5px] text-muted">
                {storeOK ? t("pub.store.text") : t("pub.store.off")}</span></span>
            </label>
          </div>
          {target.target === "git" && <GitSettings data={data} actions={actions} writer={writer} />}
          {writer && target.target === "store" && (
            <div class="mt-4">
              <button type="button" class="btn" disabled={!!busy} onClick={() => act("target", () => actions.setPublishing({ target: "store" }))}>
                {busy === "target" ? t("pub.saving") : t("pub.saveDestination")}</button>
            </div>
          )}
        </section>

        <section>
          <div class="flex flex-wrap items-end gap-3">
            <h3 class="m-0 flex-1 text-[15px] font-semibold">{t("pub.versions")}</h3>
            {writer && (
              <>
                <label class="flex flex-col gap-1">
                  <span class="text-[12px] font-semibold text-ink2">{t("pub.newVersion")}</span>
                  <input class="field w-32 font-mono" value={ver} onInput={(e) => setVer((e.target as HTMLInputElement).value)} />
                </label>
                <button type="button" class="btn btn-pri" disabled={!!busy || blocking > 0 || !ver}
                  title={blocking > 0 ? t("pub.blockingTitle", { count: blocking }) : t("pub.freezeTitle")}
                  onClick={() => act("create", () => actions.createVersion(ver).then(() => setVer("")))}>
                  {busy === "create" ? t("pub.checking") : ver ? t("pub.create", { version: ver, rev: p.head_rev }) : t("pub.createAny", { rev: p.head_rev })}
                </button>
              </>
            )}
          </div>
          {blocking > 0 && <p class="mb-0 mt-2 text-[13px] text-warn">{t("pub.blocking", { count: blocking })}</p>}
          {err && <p class="mb-0 mt-2 text-[13px] text-crit">{err}</p>}
          <div class="mt-4 flex flex-col gap-3">
            {versions?.length === 0 && <p class="m-0 text-muted">{t("pub.none")}</p>}
            {versions?.map((v) => {
              const c = confirm[v.version] ?? { ok: false, issues: false };
              const issues = v.ai_review?.verdict === "issues";
              return (
                <article class="rounded-xl border border-line bg-side p-4">
                  <div class="flex flex-wrap items-center gap-3">
                    <span class="font-mono text-[16px] font-semibold">{v.version}</span>
                    <span class={`pill ${statusStyle[v.status]}`}>{statusText(v.status)}</span>
                    <span class="text-[12.5px] text-muted">r{v.rev} · {v.target === "git" ? t("pub.targetGit") : "gIoT Store"}</span>
                    <span class="flex-1" />
                    {v.external_ref && <span class="font-mono text-[12px] text-dim">{v.external_ref}</span>}
                    {writer && ["submitted", "in_review"].includes(v.status) && (
                      <button type="button" class="btn h-7" disabled={!!busy} onClick={() => act("refresh" + v.version, () => actions.refreshVersion(v.version))}>{t("pub.refresh")}</button>
                    )}
                  </div>
                  {v.notice?.error && <p class="mb-0 mt-2 text-[13px] text-crit">{v.notice.error}</p>}
                  {v.ai_review && (
                    <div class={`mt-3 rounded-lg border px-3 py-2.5 text-[13px] ${issues ? "border-warn/40 bg-warn-soft/40" : "border-good/40 bg-good-soft/40"}`}>
                      <div class="font-semibold">{issues ? t("pub.ai.issues") : t("pub.ai.ready")}</div>
                      <p class="mb-1 mt-1 text-ink2">{v.ai_review.summary}</p>
                      {v.ai_review.findings.length > 0 && (
                        <ul class="m-0 pl-5">
                          {v.ai_review.findings.map((f) => (
                            <li class="my-0.5"><span class={f.severity === "blocker" ? "text-crit" : f.severity === "warning" ? "text-warn" : "text-muted"}>{severity(f.severity)}</span>
                              {f.key && <code class="mx-1 font-mono text-[12px] text-accent2">{f.key}</code>} {f.text}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  {writer && ["ready", "reviewed", "failed", "checks_failed", "rejected"].includes(v.status) && (
                    <div class="mt-3 flex flex-wrap items-center gap-3">
                      <button type="button" class="btn" disabled={!!busy || data.aiPaused} onClick={() => act("review" + v.version, () => actions.reviewVersion(v.version))}>
                        <Icon name="spark" size={14} stroke={2} />{busy === "review" + v.version ? t("pub.reviewing") : v.ai_review ? t("pub.reviewAgain") : t("pub.aiReview")}
                      </button>
                      {v.status === "reviewed" && (
                        <>
                          <label class="flex items-center gap-2 text-[13px]">
                            <input type="checkbox" checked={c.ok} onChange={() => setConfirm({ ...confirm, [v.version]: { ...c, ok: !c.ok } })} />
                            {t(v.target === "git" ? "pub.confirmGit" : "pub.confirmStore", { version: v.version })}
                          </label>
                          {issues && (
                            <label class="flex items-center gap-2 text-[13px] text-warn">
                              <input type="checkbox" checked={c.issues} onChange={() => setConfirm({ ...confirm, [v.version]: { ...c, issues: !c.issues } })} />
                              {t("pub.despite")}
                            </label>
                          )}
                          <button type="button" class="btn btn-pri" disabled={!!busy || !c.ok || (issues && !c.issues)}
                            onClick={() => act("publish" + v.version, () => actions.publishVersion(v.version, issues && c.issues))}>
                            {busy === "publish" + v.version ? t("pub.publishing") : t("pub.publish")}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
